#!/usr/bin/env node

import type { FigmaIconsConfigFile } from './types'
import { existsSync } from 'node:fs'
import { resolve } from 'node:path'
import process from 'node:process'
import { config as loadEnv } from 'dotenv'
import { createJiti } from 'jiti'
import { fetchAllIconsFromFigma, filterAndProcessIcons } from './sync'

// 自动加载 .env.local 文件（如果存在）
const envPath = resolve(process.cwd(), '.env.local')
if (existsSync(envPath)) {
  loadEnv({ path: envPath })
}

const colors = {
  reset: '\x1B[0m',
  red: '\x1B[31m',
  green: '\x1B[32m',
  yellow: '\x1B[33m',
  cyan: '\x1B[36m',
}

function log(message: string, color: keyof typeof colors = 'reset') {
  console.log(`${colors[color]}${message}${colors.reset}`)
}

function showHelp() {
  log('\n📦 Figma Icons Sync - 从 Figma 同步图标到 Iconify JSON\n', 'cyan')
  log('用法:', 'cyan')
  log('  figma-icons [options]\n')

  log('参数:', 'cyan')
  log('  --config       配置文件路径 (默认: figma-icons.config.ts)')
  log('  --force, -f    强制同步，跳过所有缓存')
  log('  --help, -h     显示帮助\n')

  log('环境变量:', 'cyan')
  log('  FIGMA_TOKEN    Figma API Token (必需)')
  log('  FIGMA_FILE_ID  Figma 文件 ID (必需)\n')

  log('配置文件示例 (figma-icons.config.ts):', 'cyan')
  log(`  import { defineConfig } from '@creatly/figma-icons'

  export default defineConfig({
    prefix: 'local',
    output: 'json/icons.json',
    collections: [
      {
        pages: ['Icons'],
        convertToCurrentColor: true,
      },
      {
        pages: ['Icons-Colored'],
        convertToCurrentColor: false,
      },
    ],
    depth: 4,
  })\n`, 'yellow')

  log('使用方式:', 'cyan')
  log('  1. 创建 .env.local 文件，添加 FIGMA_TOKEN 和 FIGMA_FILE_ID')
  log('  2. 创建 figma-icons.config.ts 配置文件')
  log('  3. 运行: figma-icons')
  log('  4. 强制同步: figma-icons --force\n')
}

async function main() {
  const args = process.argv.slice(2)

  // 显示帮助
  if (args.includes('--help') || args.includes('-h')) {
    showHelp()
    process.exit(0)
  }

  // 解析参数
  function getArg(name: string, fallback?: string): string | undefined {
    const index = args.indexOf(`--${name}`)
    return index !== -1 && args[index + 1]
      ? args[index + 1]
      : fallback
  }

  // 检查是否强制同步（跳过缓存）
  const force = args.includes('--force') || args.includes('-f')

  // 获取环境变量
  const fileId = process.env.FIGMA_FILE_ID
  const token = process.env.FIGMA_TOKEN

  // 验证必需的环境变量
  if (!fileId || !token) {
    log('❌ 缺少必需的环境变量\n', 'red')
    log('请设置以下环境变量:', 'yellow')
    log('  FIGMA_FILE_ID  - Figma 文件 ID')
    log('  FIGMA_TOKEN    - Figma API Token\n')
    log('使用方式: node --env-file=.env.local dist/cli.js\n')
    process.exit(1)
  }

  // 读取配置文件
  const configArg = getArg('config', 'figma-icons.config.ts')
  const configPath = configArg ?? 'figma-icons.config.ts'

  // 尝试多个配置文件扩展名
  const possiblePaths = [
    resolve(process.cwd(), configPath),
    resolve(process.cwd(), 'figma-icons.config.ts'),
    resolve(process.cwd(), 'figma-icons.config.js'),
    resolve(process.cwd(), 'figma-icons.config.json'),
  ]

  let configFilePath: string | undefined
  for (const path of possiblePaths) {
    if (existsSync(path)) {
      configFilePath = path
      break
    }
  }

  if (!configFilePath) {
    log(`❌ 无法找到配置文件\n`, 'red')
    log('尝试的路径:', 'yellow')
    possiblePaths.forEach(p => log(`  - ${p}`))
    log('\n运行 figma-icons --help 查看配置文件示例\n')
    process.exit(1)
  }

  let config: FigmaIconsConfigFile

  try {
    // 使用 jiti 加载配置文件 (支持 .ts, .js, .json)
    const jiti = createJiti(import.meta.url)
    const loadedConfig = jiti(configFilePath)
    config = loadedConfig.default || loadedConfig
  }
  catch (error) {
    log(`❌ 无法加载配置文件: ${configFilePath}\n`, 'red')
    log('请确保配置文件格式正确\n', 'yellow')
    log('运行 figma-icons --help 查看配置文件示例\n')
    console.error(error)
    process.exit(1)
  }

  // 验证配置文件
  if (!config.collections || config.collections.length === 0) {
    log('❌ 配置文件中没有定义任何图标集合\n', 'red')
    process.exit(1)
  }

  if (!config.prefix || !config.output) {
    log('❌ 配置文件缺少必需的字段: prefix 或 output\n', 'red')
    process.exit(1)
  }

  log('🚀 开始同步图标...', 'cyan')
  log(`📄 配置文件: ${configFilePath}`)
  log(`📦 图标来源数量: ${config.collections.length}`)
  log(`🎨 输出前缀: ${config.prefix}`)
  log(`📁 输出文件: ${config.output}\n`)

  try {
    // 收集所有需要的 pages
    const allPages = [...new Set(config.collections.flatMap(c => c.pages))]

    // 一次性从 Figma 获取所有图标（只调用一次 API）
    log(`\n📦 正在获取所有图标 (Pages: ${allPages.join(', ')})...`, 'cyan')
    const fetchResult = await fetchAllIconsFromFigma({
      fileId,
      token,
      prefix: config.prefix,
      depth: config.depth ?? 2,
      cacheDir: config.cacheDir ?? '.figma-cache',
      excludeNodeIds: config.excludeNodeIds,
      force,
      allPages,
    })

    if (!fetchResult) {
      log(`\n✅ 图标未修改，无需更新`, 'green')
      process.exit(0)
    }

    const { iconSet: allIconsSet, pageMap } = fetchResult

    // 创建合并的 JSON 对象
    const mergedJSON: any = {
      prefix: config.prefix,
      icons: {},
      width: undefined,
      height: undefined,
    }

    let totalIcons = 0

    // 遍历所有集合，在本地过滤和处理图标
    for (const collection of config.collections) {
      log(`\n📌 处理来源: Pages [${collection.pages.join(', ')}]`, 'cyan')
      log(`   转换为 currentColor: ${collection.convertToCurrentColor ?? true}`)

      // 过滤并处理当前 collection 的图标
      const processedIconSet = await filterAndProcessIcons(
        allIconsSet,
        pageMap,
        {
          pages: collection.pages,
          convertToCurrentColor: collection.convertToCurrentColor ?? true,
        },
      )

      const iconCount = processedIconSet.count()
      log(`   ✓ 处理 ${iconCount} 个图标`)

      // 合并图标到 JSON
      const iconSetJSON = processedIconSet.export()

      // 保留图标的原始尺寸，不强制标准化
      // 这样可以支持不同尺寸的图标（如 64x16 的品牌标识）
      // 通过 CSS 控制显示大小，保持灵活性

      // Iconify Tools 会省略默认值（如 height: 16），需要补全
      for (const iconName in iconSetJSON.icons) {
        const icon = iconSetJSON.icons[iconName]

        // 如果有 width 但没有 height，根据 width 推断 height
        // 对于非正方形图标（如 brand 64x16），需要明确设置 height
        if (icon.width && !icon.height) {
          // 默认假设是正方形，或者使用图标集的默认 height
          icon.height = iconSetJSON.height || 16
        }
        // 如果有 height 但没有 width，同理
        else if (icon.height && !icon.width) {
          icon.width = iconSetJSON.width || 16
        }
      }

      Object.assign(mergedJSON.icons, iconSetJSON.icons)

      // 保存默认宽高 (如果还没设置的话)
      if (mergedJSON.width === undefined && iconSetJSON.width) {
        mergedJSON.width = iconSetJSON.width
      }
      if (mergedJSON.height === undefined && iconSetJSON.height) {
        mergedJSON.height = iconSetJSON.height
      }

      totalIcons += iconCount
    }

    // 设置全局默认尺寸为 16x16
    // 大部分图标都是这个尺寸，只有特殊图标（如 brand 64x16）会覆盖
    // 这样可以减小 JSON 文件大小，符合 Iconify 最佳实践
    mergedJSON.width = 16
    mergedJSON.height = 16

    // 导出合并后的 JSON
    log(`\n💾 导出图标集...`, 'cyan')
    const { writeFile, mkdir } = await import('node:fs/promises')
    const { dirname } = await import('node:path')

    const outputPath = resolve(process.cwd(), config.output)
    await mkdir(dirname(outputPath), { recursive: true })
    await writeFile(
      outputPath,
      JSON.stringify(mergedJSON, null, 2),
      'utf-8',
    )

    // 总结
    log(`\n${'='.repeat(50)}`, 'cyan')
    log(`✅ 同步完成!`, 'green')
    log(`📊 总计: ${totalIcons} 个图标`)
    log(`📁 输出: ${config.output}`)
    log(`${'='.repeat(50)}\n`, 'cyan')
  }
  catch (error) {
    log(`\n❌ 同步失败:`, 'red')
    console.error(error)
    process.exit(1)
  }
}

main()
